const CAP_MERKLE_TREE = {
  id: 240,
  title: "El Árbol de Merkle",
  slug: "arbol-de-merkle",
  type: "lesson",
  category: "bitcoin-avanzado",
  level: "advanced",
  preview:
    "El Árbol de Merkle es una estructura de datos fundamental en Bitcoin. Permite verificar grandes cantidades de transacciones mediante un único hash llamado Merkle Root.",
  sections: [
    {
      heading: "¿Qué es un Árbol de Merkle?",
      text: [
        "Dentro de cada bloque de Bitcoin se encuentra una estructura llamada **Árbol de Merkle**, utilizada para organizar, verificar y resumir todas las transacciones del bloque.",
        "Su función principal es relacionar cada transacción con un único hash final llamado **Merkle Root**, que representa criptográficamente todo el conjunto."
      ],
      notes: [
        "El Merkle Root es el hash incluido en la cabecera del bloque y forma parte esencial del mecanismo de seguridad de Bitcoin."
      ]
    },

    {
      heading: "Referencia del Whitepaper",
      text: [
        "En palabras de Satoshi Nakamoto:",
        "“Una vez que la última transacción está enterrada bajo suficientes bloques, las transacciones gastadas anteriores a esta pueden ser descartadas para ahorrar espacio en disco. […] Las transacciones se comprueban en un árbol de Merkle, con solo la raíz incluida en el hash del bloque.”"
      ]
    },

    {
      heading: "Diagrama del Árbol de Merkle",
      image: {
        src: "https://raw.githubusercontent.com/AppsDevsLeon/ImagesBooks/refs/heads/main/imagesFundamentosBlockchain/Biucoinmaster/que_es_arbol_merkle.jpg",
        alt: "Diagrama visual del Árbol de Merkle mostrando hojas, ramas y Merkle Root",
        caption:
          "El Árbol de Merkle organiza todas las transacciones del bloque como hojas, agrupándolas en pares hasta formar un único hash final: la Merkle Root."
      },
      text: [
        "Este diagrama te ayudará a visualizar cómo cada transacción pasa por un proceso de hashing en capas hasta llegar al Merkle Root."
      ]
    },

    {
      heading: "¿Cómo funciona un Árbol de Merkle?",
      text: [
        "Un Árbol de Merkle agrupa todas las transacciones del bloque de dos en dos y calcula un hash de cada par.",
        "Los hashes resultantes se vuelven a agrupar en pares, generando un nuevo hash superior, y así sucesivamente hasta llegar al hash final: **el Merkle Root**.",
        "Cualquier cambio en una transacción altera su hash, luego su nodo padre, después su nodo abuelo y así sucesivamente hasta el Merkle Root, invalidando el bloque."
      ],
      bluepoints: [
        "Relaciona todos los datos con un solo hash final.",
        "Si una transacción cambia, todo el árbol cambia.",
        "Permite verificar datos sin revisar todas las transacciones.",
        "Aumenta la seguridad y reduce el peso de los datos."
      ]
    },

    {
      heading: "Características de los Árboles de Merkle",
      text: [
        "Los árboles Merkle poseen características especiales que los hacen extremadamente eficientes y seguros."
      ],
      list: [
        "Estructura eficiente para organizar datos distribuidos.",
        "Alta resistencia a alteraciones en los datos.",
        "Rendimiento excepcional en redes distribuidas como Bitcoin.",
        "Computacionalmente eficientes de crear y verificar.",
        "Permiten “disección” o verificación parcial sin revisar todo el árbol.",
        "Reducen la cantidad de almacenamiento necesario.",
        "Flexibles: usados en bases de datos, sistemas de archivos, P2P, llaves públicas, versionamiento, etc."
      ]
    }
  ]
};

export default CAP_MERKLE_TREE;
